import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { SwPush } from '@angular/service-worker';
import { ENVIRONMENT } from '@sona/angular-util-environment';
import { BehaviorSubject, from, of, Subject, tap } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';


@Injectable({
    providedIn: 'root'
})
export class WebNotificationService {
    private readonly _swPush = inject(SwPush);
    private readonly _http = inject(HttpClient);

    private permissionSubject = new BehaviorSubject<NotificationPermission>(Notification.permission);
    readonly permission$ = this.permissionSubject.asObservable();
    readonly permission = toSignal(this.permissionSubject, { initialValue: this.permissionSubject.value });

    private readonly _requestSubject = new Subject<void>();

    constructor() {
        const environment = inject(ENVIRONMENT);

        this._requestSubject
            .pipe(
                switchMap(() => from(this._swPush.requestSubscription({
                    serverPublicKey: environment.vapid.publicKey
                }))),
                switchMap((sub) => {
                    this.permissionSubject.next('granted');

                    return this.registerOnServer(sub);
                }),
                tap(() => {
                    this.permissionSubject.next(Notification.permission);
                }),
                catchError((error) => {
                    this.permissionSubject.next(Notification.permission);

                    if (!(error instanceof DOMException && error.name === 'NotAllowedError')) {
                        console.error('Error while requesting subscription:', error);
                    }

                    return of(null);
                }),
                takeUntilDestroyed()
            )
            .subscribe();
    }

    get isEnabled() {
        return Notification.permission === "granted";
    }

    /**
     * send a request to the server to register or update the current subscription data of a client
     * @param params - the subscription data
     */
    registerOnServer(params: PushSubscription) {
        return this._http.post(
            `/api/notifications/register`,
            params
        );
    }

    /**
     * request the browser to show a notification to the user if he did not yet decline it
     */
    requestSubscription() {
        if (!this._swPush.isEnabled) {
            return ;
        }

        this._requestSubject.next();
    }

    /**
     * Check if the permission subject has changed (i.e. if user changed the permission in the browser) and refresh it if needed
     */
    refreshPermission() {
        if (this.permissionSubject.value !== Notification.permission) {
            this.permissionSubject.next(Notification.permission);
        }
    }

    /**
     * unsubscribe the user from service worker and backend
     */
    async unsubscribeSubscription() {
        await this._swPush.unsubscribe();

        return this._http.post("/api/notifications/unsubscribe", {});
    }
}
